import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import {
  recordingSessionApi,
  type RecordingSessionDTO,
  type RecordingSessionMode,
  type RecordingSessionConsentMethod,
  type RecordingSessionSoapDraft,
  type RecordingSessionStages,
  type RecordingSessionTranscriptSegmentDTO,
} from '@/services/recordingSessionApi';
import { audioRetentionApi } from '@/services/audioRetentionApi';
import {
  shouldNudgeInstallOnRecord,
  markInstallNudgeShown,
} from '@/lib/installPrompt';
import { clearCompletionIntent, hasCompletionIntent } from '@/lib/completionIntent';
import { markTabAsRecorder, isTabRecorder } from '@/lib/recordingTabMarker';
import { useVoiceStore } from '@/stores/useVoiceStore';
import { useAuthStore } from '@/stores/useAuthStore';
import { Permission } from '@/types/user';

/**
 * Session-storage flag set when the doctor checks "don't ask again this
 * session" on the attestation modal. Cleared on tab close (sessionStorage
 * scope). We deliberately don't use localStorage — the attestation is
 * about *this* clinic visit cohort, not a permanent silence.
 */
const ATTESTATION_DISMISSED_KEY = 'maie.audioRetention.attestationDismissedThisSession';

const isAttestationDismissed = (): boolean => {
  try {
    return sessionStorage.getItem(ATTESTATION_DISMISSED_KEY) === 'true';
  } catch {
    return false;
  }
};

const markAttestationDismissed = (): void => {
  try {
    sessionStorage.setItem(ATTESTATION_DISMISSED_KEY, 'true');
  } catch {
    // sessionStorage unavailable — fall through; the modal just shows again
    // on next click. Not fatal.
  }
};

/**
 * Backend-backed recording session (BD-9). The store is a thin cache over
 * `RecordingSession` rows in the DB; all mutations round-trip to the
 * backend, so the session survives page reloads and cross-device sign-in.
 *
 * Public API preserved from the pre-BD-9 frontend-only store
 * (`startSession`, `assignPatient`, `pause`, `resume`, `stop`, `tick`)
 * so existing callers (RecordingBanner, DoctorHome, etc.) do not churn.
 * New action: `ensureCommitted()` (BD-10) — idempotent commit to draft record.
 */

export interface RecordingSession {
  /** Backend RecordingSession.id (persistent, survives reload). */
  id: string;
  patientId?: string;
  patientNameEn?: string;
  patientNameAr?: string;
  startedAt: number;
  paused: boolean;
  /** ms accumulated *active* time, sealed at the last pause/stop. The current
   *  capture window (if status === 'capturing') is added on top via
   *  `lastResumedAt`. Server-authoritative; survives across devices. */
  durationMs: number;
  /** ms since epoch of the most recent resume/start; null when paused/stopped.
   *  `now - lastResumedAt + durationMs` is the live elapsed time. */
  lastResumedAtMs: number | null;
  /** Convenience: whole-seconds form of the live elapsed time at this state's
   *  serialization moment. Live UI should re-derive every render via
   *  `getLiveElapsedSeconds()` so paused time doesn't visually drift. */
  elapsedSeconds: number;
  /** True when no patient is assigned (ambient capture). */
  unassigned: boolean;
  status: RecordingSessionDTO['status'];
  sessionMode: RecordingSessionMode;
  language: string;
  /**
   * Capture mode chosen in the arm panel: 'visit' (doctor + patient, diarized
   * with speaker labels) or 'dictate' (single voice, no labels — cleaner and,
   * once the pipeline honors it, faster). Client-only for now (not persisted
   * on the backend row), so a cross-device rehydrate falls back to 'visit'.
   */
  captureMode: 'visit' | 'dictate';
  /** Set once commitToRecord succeeds. */
  medicalRecordId?: string;
  /** Server-persisted transcript snapshot (flat text). Updated by the
   *  scribe processor on every partial-transcription emission. Used to
   *  rehydrate the live transcript pane after a refresh / cross-device
   *  handover so the doctor sees what was already captured. */
  transcript?: string | null;
  /** Server-persisted diarized turns matching `transcript`. The dock
   *  prefers these when seeding the live pane after a refresh/handover
   *  so speaker chips survive the reload; falls back to the flat
   *  transcript for rows predating the column. */
  transcriptSegments?: RecordingSessionTranscriptSegmentDTO[] | null;
  /** Server-persisted SOAP draft. Set when the scribe processor emits
   *  `scribe.soap-result`; the dock polls this so a refresh/handover
   *  during synth still recovers the result. Non-null === synth done. */
  soapDraft?: RecordingSessionSoapDraft | null;
  /**
   * Template id this consult should be synthesized against. Persisted on
   * `recording_sessions.template_id` so a refresh / cross-device handover
   * keeps the picked template, and is copied onto
   * `medical_records.template_id` at commit time so the review page
   * renders the same shape that was scribed into. Also plumbed through
   * to the live gateway via `scribe.start-session.templateId`.
   */
  templateId?: string | null;
  /**
   * Pipeline-stage tracker mirrored from the server. The recovery driver
   * reads `stages.synth` to decide whether synth needs driving on a
   * refresh / cross-device handover (server-authoritative). Falls back to
   * the localStorage `completionIntent` flag when stages is unavailable.
   */
  stages?: RecordingSessionStages | null;
}

/**
 * Compute the *current* elapsed active time from a session snapshot. The
 * live recording surface should call this each render rather than reading
 * `elapsedSeconds` directly, so the timer counts up smoothly while
 * capturing and freezes precisely on pause. Cross-device-safe: any client
 * with the same DTO arrives at the same number (modulo clock skew).
 */
export const getLiveElapsedSeconds = (
  session: Pick<RecordingSession, 'durationMs' | 'lastResumedAtMs' | 'paused' | 'status'> | null | undefined,
  now: number = Date.now(),
): number => {
  if (!session) return 0;
  const sealed = Math.max(0, session.durationMs || 0);
  // Capturing — add the current resume window. Paused/stopped/committed/
  // abandoned all freeze at `durationMs`; the server already flushed the
  // last window into it on the state transition.
  if (session.status === 'capturing' && session.lastResumedAtMs) {
    const live = Math.max(0, now - session.lastResumedAtMs);
    return Math.floor((sealed + live) / 1000);
  }
  return Math.floor(sealed / 1000);
};

export interface StartSessionInit {
  patientId?: string;
  patientNameEn?: string;
  patientNameAr?: string;
  sessionMode?: RecordingSessionMode;
  language?: string;
  consentMethod?: RecordingSessionConsentMethod;
  /** Template the doctor picked before starting; carried into the dock so
   *  scribe.start-session can forward it. Optional — undefined falls back
   *  to the default SOAP shape. */
  templateId?: string;
  /** Capture mode the doctor confirmed in the arm panel. Defaults to the
   *  doctor's `defaultCaptureMode` pref when omitted. */
  captureMode?: 'visit' | 'dictate';
  /** Set by the arm panel when it has already shown the patient-consent
   *  attestation inline — suppresses the separate RecordingAttestationModal
   *  so consent isn't confirmed twice. Client-only; never sent to the API. */
  skipAttestationGate?: boolean;
  /** Originating appointment id. When set, the backend persists it into
   *  the session's metadata and threads it onto the produced
   *  medical_record so the schedule tile links to the consult. */
  appointmentId?: string;
}

interface RecordingSessionState {
  session: RecordingSession | null;
  /**
   * Pending "arm" intent — a cold-mic pre-roll before any backend session
   * exists. Set by `armSession()` from every tap-to-record entry point; the
   * globally-mounted arm panel renders off it (Start button + summary +
   * Change). Only when the doctor taps Start does the real `startSession()`
   * fire. Transient — never persisted. This is why quick-starts no longer go
   * hot instantly and capture the room before the doctor is ready.
   */
  armIntent: StartSessionInit | null;
  /** True while a backend round-trip is in flight. */
  pending: boolean;
  error: string | null;
  /**
   * Set when `startSession` was called while AI-training retention is
   * active and the doctor hasn't yet attested for this browser session.
   * The actual session creation is deferred until `confirmAttestation`
   * fires from the modal. Transient — never persisted.
   */
  awaitingAttestation: StartSessionInit | null;
  /**
   * Set when `startSession` was called from a non-installed mobile/iPad
   * web context for the first time on this device. Resolved by the
   * InstallNudgeModal calling `confirmInstallNudge` (proceed) or
   * `cancelInstallNudge` (back out). Mirrors the awaitingAttestation
   * gate. Transient — never persisted.
   */
  awaitingInstallNudge: StartSessionInit | null;

  startSession: (init: StartSessionInit) => Promise<RecordingSession | null>;
  /** Open the cold-mic arm panel with this intent (no backend call yet). */
  armSession: (init: StartSessionInit) => void;
  /** Dismiss the arm panel without starting (doctor backed out). */
  cancelArm: () => void;
  /** Confirm attestation modal — proceeds with the deferred init.
   *  `remember=true` suppresses the modal for the rest of the
   *  browser session (sessionStorage). */
  confirmAttestation: (
    remember: boolean,
  ) => Promise<RecordingSession | null>;
  /** Doctor backed out of the attestation — drop the deferred init. */
  cancelAttestation: () => void;
  /** Doctor closed the install nudge → proceed with recording (the
   *  attestation gate may still fire after this). */
  confirmInstallNudge: () => Promise<RecordingSession | null>;
  /** Doctor backed out of the install nudge — drop the deferred init. */
  cancelInstallNudge: () => void;

  assignPatient: (p: {
    patientId: string;
    patientNameEn?: string;
    patientNameAr?: string;
  }) => Promise<void>;
  tick: () => void;
  pause: () => Promise<void>;
  resume: () => Promise<void>;
  stop: () => Promise<void>;
  abandon: () => Promise<void>;
  ensureCommitted: () => Promise<{ id: string; patientId: string } | null>;

  /** Re-fetch the backend session so a page reload sees a live session. */
  rehydrate: () => Promise<void>;
  /**
   * Load a specific session into the active slot. Used by Pending
   * Recordings → Retry to point the dock at a stuck session so its
   * recovery driver picks it up. No-ops when the session is already
   * terminal (committed/abandoned) or unreachable.
   */
  loadSessionById: (id: string) => Promise<RecordingSession | null>;
}

// bigint columns arrive as strings from the JSON response — coerce.
const numFromMaybeString = (v: unknown): number => {
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  if (typeof v === 'string') {
    const n = Number.parseInt(v, 10);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
};

const fromDto = (
  dto: RecordingSessionDTO,
  namesOverride?: Pick<RecordingSession, 'patientNameEn' | 'patientNameAr'>,
  prior?: RecordingSession | null,
): RecordingSession => {
  const startedAtMs = new Date(dto.startedAt).getTime();
  const startedAtSafe = Number.isFinite(startedAtMs) ? startedAtMs : Date.now();
  const lastResumedAtMs = dto.lastResumedAt
    ? new Date(dto.lastResumedAt).getTime()
    : null;
  const durationMs = Math.max(0, numFromMaybeString(dto.durationMs));
  const paused = dto.status === 'paused';

  // Snapshot of "now" elapsed for any non-live consumer (e.g. summary
  // labels). The live recording surface should call getLiveElapsedSeconds
  // every render; this is just a convenient initial value.
  const elapsedSeconds = getLiveElapsedSeconds(
    {
      durationMs,
      lastResumedAtMs: Number.isFinite(lastResumedAtMs as number)
        ? (lastResumedAtMs as number)
        : null,
      paused,
      status: dto.status,
    },
  );

  return {
    id: dto.id,
    patientId: dto.patientId ?? undefined,
    patientNameEn: namesOverride?.patientNameEn ?? prior?.patientNameEn,
    patientNameAr: namesOverride?.patientNameAr ?? prior?.patientNameAr,
    startedAt: startedAtSafe,
    paused,
    durationMs,
    lastResumedAtMs:
      lastResumedAtMs !== null && Number.isFinite(lastResumedAtMs)
        ? lastResumedAtMs
        : null,
    elapsedSeconds,
    unassigned: !dto.patientId,
    status: dto.status,
    sessionMode: dto.sessionMode,
    language: dto.language,
    // captureMode isn't persisted server-side — carry it from the prior
    // client snapshot across rehydrate/reload; default to 'visit' when a
    // fresh row arrives (safe: diarized, speaker labels shown).
    captureMode: prior?.captureMode ?? 'visit',
    medicalRecordId: dto.medicalRecordId ?? undefined,
    transcript: dto.transcript ?? prior?.transcript ?? null,
    transcriptSegments:
      dto.transcriptSegments ?? prior?.transcriptSegments ?? null,
    soapDraft: dto.soapDraft ?? prior?.soapDraft ?? null,
    templateId: dto.templateId ?? prior?.templateId ?? undefined,
    stages: dto.stages ?? prior?.stages ?? null,
  };
};

const isRecoverableSession = (dto: RecordingSessionDTO): boolean =>
  dto.status !== 'committed' && dto.status !== 'abandoned';

const shouldMountDtoAsActiveSession = (dto: RecordingSessionDTO): boolean =>
  dto.status === 'capturing' ||
  dto.status === 'paused' ||
  (dto.status === 'stopped' && hasCompletionIntent(dto.id));

const shouldMountSessionAsActiveSession = (session: RecordingSession): boolean =>
  session.status === 'capturing' ||
  session.status === 'paused' ||
  (session.status === 'stopped' && hasCompletionIntent(session.id));

const pickLatestRecoverableSession = (
  sessions: RecordingSessionDTO[],
): RecordingSessionDTO | null => {
  const candidates = sessions.filter(
    (session) =>
      isRecoverableSession(session) && shouldMountDtoAsActiveSession(session),
  );
  if (candidates.length === 0) return null;
  return candidates
    .slice()
    .sort(
      (a, b) =>
        new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime(),
    )[0];
};

/**
 * Real session-start path — round-trips to the backend, normalizes the
 * DTO, writes the result to the store. Held outside the create() body
 * so both `startSession` (no attestation needed / dismissed) and
 * `confirmAttestation` can call it without duplicating the logic.
 *
 * Mirrors the original startSession body exactly — preserved 1:1.
 */
async function _startSessionInternal(
  set: (
    partial:
      | Partial<RecordingSessionState>
      | ((s: RecordingSessionState) => Partial<RecordingSessionState>),
  ) => void,
  init: StartSessionInit,
): Promise<RecordingSession | null> {
  set({ pending: true, error: null });
  try {
    const dto = await recordingSessionApi.start({
      patientId: init.patientId,
      sessionMode: init.sessionMode ?? 'ambient',
      // Stamp the doctor's chosen scribe language on the row so the
      // reprocess/auto-retry path (which reads session.language) re-runs in
      // the same language. Defaults to English.
      language:
        init.language ?? useVoiceStore.getState().settings.sttLanguage ?? 'en',
      consentMethod: init.consentMethod,
      appointmentId: init.appointmentId,
      templateId: init.templateId,
    });
    if (!dto?.id) {
      set({
        pending: false,
        error:
          'Recording session started but the server response was malformed (missing id). Try again or refresh.',
      });
      return null;
    }
    const session: RecordingSession = {
      ...fromDto(dto, {
        patientNameEn: init.patientNameEn,
        patientNameAr: init.patientNameAr,
      }),
      // Templates aren't persisted on the recording_sessions row yet, so
      // the picked template lives only in this client snapshot. A page
      // reload mid-recording falls back to the default SOAP shape.
      templateId: init.templateId ?? undefined,
      // Capture mode from the arm panel; falls back to the doctor's default
      // pref for direct (non-armed) starts like the DoctorHome mic FAB.
      captureMode:
        init.captureMode ??
        useVoiceStore.getState().settings.defaultCaptureMode ??
        'visit',
    };
    // Tag THIS tab as the recorder before useLiveRecording even mounts.
    // Without this the dock's cold-mount gate sees a `capturing` session
    // with no marker and refuses to acquire the mic — surfacing the
    // recovery banner on a fresh consult instead of starting capture.
    // The hook still re-marks on every successful start as a belt-and-
    // suspenders signal; this just covers the synchronous gap between
    // server-start and the hook running its first effect.
    markTabAsRecorder(session.id);
    // A session now owns the mic — drop any lingering arm intent so the arm
    // panel can't shadow the live dock.
    set({ session, pending: false, armIntent: null });
    return session;
  } catch (err) {
    // ENCOUNTER_ACTIVE (HTTP 409) — backend refused because the
    // provider already has a non-terminal session. Adopt the existing
    // one into the local store instead of leaving the doctor with a
    // toast and no path forward. The dock then surfaces the in-flight
    // consult so they can complete / discard before starting a new one.
    const errAny = err as {
      response?: {
        status?: number;
        data?: {
          error?: { code?: string; existingSessionId?: string };
          code?: string;
          existingSessionId?: string;
        };
      };
      status?: number;
    } | undefined;
    const status = errAny?.response?.status ?? errAny?.status;
    const payload = errAny?.response?.data;
    const code =
      payload?.error?.code ?? payload?.code ?? '';
    const existingSessionId =
      payload?.error?.existingSessionId ?? payload?.existingSessionId;
    if (status === 409 && code === 'ENCOUNTER_ACTIVE' && existingSessionId) {
      try {
        const existing = await recordingSessionApi.get(existingSessionId);
        markTabAsRecorder(existing.id);
        set({
          session: fromDto(existing, {
            patientNameEn: init.patientNameEn,
            patientNameAr: init.patientNameAr,
          }),
          pending: false,
          error:
            'Another encounter is still active — pick up where you left off, or discard it first.',
        });
        return null;
      } catch {
        // Fall through to the generic error path below.
      }
    }
    set({
      pending: false,
      error:
        err instanceof Error
          ? err.message
          : 'Failed to start recording session',
    });
    return null;
  }
}

/**
 * Run the AI-training attestation gate, then dispatch the real start.
 * Extracted so both the entry-point `startSession` and the
 * `confirmInstallNudge` resume path share one path through attestation
 * instead of duplicating the consent fetch + modal-defer dance.
 */
async function _runAttestationThenStart(
  set: (
    partial:
      | Partial<RecordingSessionState>
      | ((s: RecordingSessionState) => Partial<RecordingSessionState>),
  ) => void,
  init: StartSessionInit,
): Promise<RecordingSession | null> {
  // The attestation is a PATIENT-consent checkpoint ("I informed the patient
  // and obtained consent"). With no patient assigned — the doctor is just
  // dictating a note — there's no one to consent, so the gate doesn't apply.
  // (The arm panel also folds consent inline when a patient IS present.)
  // `skipAttestationGate` is set by that inline path so the modal doesn't
  // double up.
  if (!init.patientId || init.skipAttestationGate) {
    return _startSessionInternal(set, init);
  }
  if (!isAttestationDismissed()) {
    let needsAttestation = false;
    try {
      const consent = await audioRetentionApi.myConsent();
      needsAttestation = consent.effective;
    } catch {
      // Default to "no gate" on any error — a transient backend hiccup
      // must never block clinical work. The audit trail lives in
      // `consent_snapshot.doctorAttestedAt` set server-side at
      // scribe-session start, so the modal is the visible UX checkpoint,
      // not the only record.
      needsAttestation = false;
    }
    if (needsAttestation) {
      set({ awaitingAttestation: init });
      return null;
    }
  }
  return _startSessionInternal(set, init);
}

export const useRecordingSessionStore = create<RecordingSessionState>()(
  persist(
    (set, get) => ({
      session: null,
      armIntent: null,
      pending: false,
      error: null,
      awaitingAttestation: null,
      awaitingInstallNudge: null,

      armSession(init) {
        const current = get().session;
        // A live session already owns the mic — don't shadow it with an arm
        // panel. (Matches startSession's capturing/paused guard.)
        if (
          current &&
          (current.status === 'capturing' || current.status === 'paused')
        ) {
          return;
        }
        set({ armIntent: init, error: null });
      },

      cancelArm() {
        set({ armIntent: null });
      },

      async startSession(init) {
        const current = get().session;
        // Only block when the existing session still owns the
        // microphone (`capturing` or `paused`). A `stopped` session
        // is post-mic — synth is baking server-side and the doctor
        // is free to start the next consult; the backend's
        // ENCOUNTER_ACTIVE guard mirrors this exact predicate.
        // Without this distinction, the synth-overlay running in
        // the background would trap the doctor on the previous
        // consult until the LLM finished, which can take 30–60 s.
        if (
          current &&
          (current.status === 'capturing' || current.status === 'paused')
        ) {
          return current;
        }
        // Double-click guard: a `pending` start request is already in
        // flight. Returning the existing session (if any) keeps the
        // caller's awaited Promise<...> contract intact without firing
        // a second POST that the backend would reject as
        // ENCOUNTER_ACTIVE anyway.
        if (get().pending) return current ?? null;

        // First-mobile-web-recording install nudge. Mirrors the
        // attestation defer pattern: park the init, surface the modal,
        // resume via confirmInstallNudge. Returns null synchronously so
        // callers don't think the session started yet. Desktop and
        // already-installed contexts skip this entirely.
        if (shouldNudgeInstallOnRecord()) {
          set({ awaitingInstallNudge: init });
          return null;
        }

        return _runAttestationThenStart(set, init);
      },

      async confirmAttestation(remember) {
        const init = get().awaitingAttestation;
        if (!init) return null;
        if (remember) markAttestationDismissed();
        set({ awaitingAttestation: null });
        return _startSessionInternal(set, init);
      },

      cancelAttestation() {
        set({ awaitingAttestation: null });
      },

      async confirmInstallNudge() {
        const init = get().awaitingInstallNudge;
        if (!init) return null;
        markInstallNudgeShown();
        set({ awaitingInstallNudge: null });
        return _runAttestationThenStart(set, init);
      },

      cancelInstallNudge() {
        // Mark shown even on cancel — the doctor saw the prompt and
        // chose not to act; nagging again next click is hostile.
        markInstallNudgeShown();
        set({ awaitingInstallNudge: null });
      },

      async assignPatient({ patientId, patientNameEn, patientNameAr }) {
        const current = get().session;
        if (!current) return;
        if (!current.id) {
          // Stale local session with no id — wipe and bail.
          set({ session: null });
          return;
        }
        set({ pending: true });
        try {
          const dto = await recordingSessionApi.assignPatient(current.id, patientId);
          set({
            session: fromDto(dto, { patientNameEn, patientNameAr }, current),
            pending: false,
          });
        } catch (err) {
          set({
            pending: false,
            error: err instanceof Error ? err.message : 'Failed to assign patient',
          });
        }
      },

      tick() {
        // Tick is now a 1Hz re-render trigger only. Elapsed time is derived
        // from `durationMs + (now - lastResumedAtMs)` via getLiveElapsedSeconds
        // (see consumers like RecordingDock). Storing a counter here used to
        // diverge from the server snapshot after every pause/resume; the
        // server is now the single source of truth and we just nudge React.
        const s = get().session;
        if (!s) return;
        if (s.status !== 'capturing') return;
        const live = getLiveElapsedSeconds(s);
        if (live === s.elapsedSeconds) return;
        set({ session: { ...s, elapsedSeconds: live } });
      },

      async pause() {
        const current = get().session;
        if (!current) return;
        if (!current.id) { set({ session: null }); return; }
        // Optimistic: flush the current capture window into durationMs and
        // null out lastResumedAtMs, mirroring what the server will do. Means
        // the timer freezes immediately on tap instead of waiting for the
        // round-trip to land.
        const now = Date.now();
        const flushed =
          current.lastResumedAtMs && current.status === 'capturing'
            ? Math.max(0, now - current.lastResumedAtMs)
            : 0;
        const optimistic: RecordingSession = {
          ...current,
          paused: true,
          status: 'paused',
          durationMs: current.durationMs + flushed,
          lastResumedAtMs: null,
        };
        optimistic.elapsedSeconds = getLiveElapsedSeconds(optimistic, now);
        set({ session: optimistic });
        try {
          const dto = await recordingSessionApi.pause(current.id);
          set({ session: fromDto(dto, undefined, optimistic) });
        } catch (err) {
          set({
            session: current,
            error: err instanceof Error ? err.message : 'Pause failed',
          });
        }
      },

      async resume() {
        const current = get().session;
        if (!current) return;
        if (!current.id) { set({ session: null }); return; }
        // Resume = "this tab is now the recorder for this session", so
        // mark it before the optimistic flip. Covers PendingRecordings'
        // resume button and the recovery-banner Resume action — both
        // route through here, and the dock's cold-mount gate would
        // otherwise re-block the mic on resume.
        markTabAsRecorder(current.id);
        const now = Date.now();
        const optimistic: RecordingSession = {
          ...current,
          paused: false,
          status: 'capturing',
          lastResumedAtMs: now,
        };
        optimistic.elapsedSeconds = getLiveElapsedSeconds(optimistic, now);
        set({ session: optimistic });
        try {
          const dto = await recordingSessionApi.resume(current.id);
          set({ session: fromDto(dto, undefined, optimistic) });
        } catch (err) {
          set({
            session: current,
            error: err instanceof Error ? err.message : 'Resume failed',
          });
        }
      },

      async stop() {
        const current = get().session;
        if (!current) return;
        set({ session: null });
        if (!current.id) return;
        try {
          await recordingSessionApi.stop(current.id);
        } catch (err) {
          // Banner has already closed; surface the error on the next open.
          set({ error: err instanceof Error ? err.message : 'Stop failed' });
        }
      },

      async abandon() {
        const current = get().session;
        if (!current) return;
        set({ session: null });
        if (!current.id) return;
        // Drop any lingering completion intent — the doctor explicitly
        // discarded this session, so the recovery driver must NOT try
        // to finish it on the next mount.
        clearCompletionIntent(current.id);
        try {
          await recordingSessionApi.abandon(current.id);
        } catch (err) {
          set({ error: err instanceof Error ? err.message : 'Abandon failed' });
        }
      },

      /** BD-10: idempotent commit. */
      async ensureCommitted() {
        const current = get().session;
        if (!current) return null;
        if (!current.id) {
          set({ session: null, error: 'Recording session is invalid; please restart.' });
          return null;
        }
        if (current.medicalRecordId) {
          return { id: current.medicalRecordId, patientId: current.patientId ?? '' };
        }
        if (!current.patientId) {
          set({
            error: 'Cannot commit a recording session without a patient assigned.',
          });
          return null;
        }
        set({ pending: true });
        try {
          const record = await recordingSessionApi.commit(current.id);
          set({
            session: {
              ...current,
              status: 'committed',
              medicalRecordId: record.id,
            },
            pending: false,
          });
          return { id: record.id, patientId: record.patientId };
        } catch (err) {
          set({
            pending: false,
            error: err instanceof Error ? err.message : 'Commit failed',
          });
          return null;
        }
      },

      async loadSessionById(id) {
        if (!id || typeof id !== 'string') return null;
        try {
          const dto = await recordingSessionApi.get(id);
          if (dto.status === 'committed' || dto.status === 'abandoned') {
            // Terminal — nothing to drive. Strip any leftover intent so
            // the next dock mount doesn't loop.
            clearCompletionIntent(id);
            set({ session: null });
            return null;
          }
          if (!shouldMountDtoAsActiveSession(dto)) {
            set({ session: null });
            return null;
          }
          const next = fromDto(dto, undefined, get().session);
          set({ session: next });
          return next;
        } catch (err) {
          set({
            error: err instanceof Error ? err.message : 'Failed to load session',
          });
          return null;
        }
      },

      async rehydrate() {
        /* A role that cannot read records (technician, accountant) has no
           recording to recover — asking would only earn a 403. */
        if (!useAuthStore.getState().roleCheck.hasPermission(Permission.MEDICAL_RECORDS_READ)) {
          if (get().session) set({ session: null });
          return;
        }
        const cached = get().session;
        // Hold-then-pause helper: if a still-`capturing` row lands on a
        // tab that isn't the recorder (sessionStorage marker absent), the
        // doctor either refreshed or opened a different tab/device. The
        // mic isn't held by anyone — we MUST pause server-side before
        // mounting the dock, otherwise the auto-acquire path would silently
        // hijack the mic with no doctor confirmation. Disconnect/reconnect
        // within the same page lifetime keeps the marker, so this branch
        // is skipped for transient WS blips.
        const autoPauseIfOrphaned = async (
          dto: RecordingSessionDTO,
        ): Promise<RecordingSessionDTO> => {
          if (dto.status !== 'capturing') return dto;
          if (isTabRecorder(dto.id)) return dto;
          try {
            return await recordingSessionApi.pause(dto.id);
          } catch {
            // Pause failed (network blip, race with another caller). The
            // dock's cold-mount gate refuses to acquire the mic anyway when
            // the marker is absent, so the worst case is the row stays
            // 'capturing' on the server until the stale-capturing cron
            // sweeps it. No user-visible breakage.
            return dto;
          }
        };
        const loadLatestMine = async () => {
          try {
            const mine = await recordingSessionApi.mine();
            const nextDto = pickLatestRecoverableSession(mine);
            if (!nextDto) {
              set({ session: null });
              return;
            }
            const settled = await autoPauseIfOrphaned(nextDto);
            set({ session: fromDto(settled, undefined, cached ?? null) });
          } catch {
            set({ session: null });
          }
        };
        if (!cached) {
          await loadLatestMine();
          return;
        }
        // Defensive: a previous bug let sessions persist with `id: undefined`,
        // which then caused every action to hit `/recording-sessions/undefined/*`
        // and 500. Drop any cached session without a real id so the user can
        // start over cleanly.
        if (!cached.id || typeof cached.id !== 'string') {
          set({ session: null });
          return;
        }
        try {
          const dto = await recordingSessionApi.get(cached.id);
          if (dto.status === 'committed' || dto.status === 'abandoned') {
            // Terminal — also drop any leftover completion intent so the
            // dock doesn't try to finalize a session that's already done
            // (committed) or thrown away (abandoned).
            clearCompletionIntent(cached.id);
            await loadLatestMine();
            return;
          }
          if (!shouldMountDtoAsActiveSession(dto)) {
            set({ session: null });
            return;
          }
          const settled = await autoPauseIfOrphaned(dto);
          set({ session: fromDto(settled, undefined, cached) });
        } catch {
          // Cached session no longer exists or is no longer accessible.
          // Fall back to the provider's latest recoverable backend row.
          await loadLatestMine();
        }
      },
    }),
    {
      name: 'maie-recording-session',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        session:
          state.session && shouldMountSessionAsActiveSession(state.session)
            ? state.session
            : null,
      }),
    },
  ),
);
